/**
 * Identity & Language Assertion Tool Configuration
 * 
 * Implements an "identity anchoring" mechanism based on the research paper
 * "Echoing: Identity Failures when LLM Agents Talk to Each Other" (arxiv 2511.09710).
 * 
 * The AI must declare its role AND language/dialect before every response.
 * This reduces role confusion ("echoing") from ~70% to ~9%, and prevents
 * dialect drift (e.g. Brazilian Portuguese instead of European Portuguese).
 * 
 * Uses enum-constrained parameters so the AI returns exact strings
 * that can be validated with === instead of brittle .includes() matching.
 * 
 * Used by both Frontend and Game apps via session-ui shared package.
 */

import { ToolDefinition } from '@selna/session';

/**
 * Maps language codes to human-readable dialect labels.
 * These labels are used as the single allowed enum value for my_language,
 * forcing the AI to actively recall which dialect to use before each response.
 */
export const LANGUAGE_DIALECT_LABELS: Record<string, string> = {
  'pt-PT': 'Português Europeu (Portugal)',
  'pt': 'Português Europeu (Portugal)',
  'es-ES': 'Castellano (Espana)',
  'es': 'Castellano (Espana)',
  'en-US': 'English (United Kingdom)',
  'en': 'English (United Kingdom)',
  'da-DK': 'Dansk (Danmark)',
  'da': 'Dansk (Danmark)',
  'fr-FR': 'Français (France)',
  'fr': 'Français (France)',
};

/**
 * Short language reminders injected into function call results.
 * The model processes these immediately before generating audio,
 * acting as last-mile priming to prevent dialect drift.
 */
/**
 * Language reminders injected as the function_call_output string.
 * 
 * CRITICAL: These are sent as plain text strings, NOT as JSON fields.
 * The model processes plain text output as a direct instruction immediately
 * before generating audio. JSON fields (e.g. { language_reminder: "..." })
 * are treated as structured data and may be ignored.
 * 
 * Format: imperative instruction in the target language itself, so the model
 * "thinks" in that language before speaking.
 */
export const LANGUAGE_REMINDERS: Record<string, string> = {
  'pt-PT': 'Estás numa conversa de voz (Real Time API). Fala em Português Europeu de Portugal — pt-PT.',
  'pt': 'Estás numa conversa de voz (Real Time API). Fala em Português Europeu de Portugal — pt-PT.',
  'es-ES': 'Estás en una conversación de voz (Real Time API). Habla en Castellano de España, con acento de España (no latinoamericano). Vocabulario: coche, ordenador, móvil, piso, vale, tío. Usa "vosotros" para el plural informal.',
  'es': 'Estás en una conversación de voz (Real Time API). Habla en Castellano de España, con acento de España (no latinoamericano). Vocabulario: coche, ordenador, móvil, piso, vale, tío. Usa "vosotros" para el plural informal.',
  'en-US': 'You are in a real-time voice conversation (Real Time API). Speak British English (UK) with a British accent, not American. Vocabulary: lift, lorry, colour, neighbourhood, flat, pavement.',
  'en': 'You are in a real-time voice conversation (Real Time API). Speak British English (UK) with a British accent, not American. Vocabulary: lift, lorry, colour, neighbourhood, flat, pavement.',
  'da-DK': 'Du er i en realtids-stemmesamtale (Real Time API). Tal dansk (fra Danmark) med dansk accent. Brug dansk ordforråd og grammatik fra Danmark.',
  'da': 'Du er i en realtids-stemmesamtale (Real Time API). Tal dansk (fra Danmark) med dansk accent. Brug dansk ordforråd og grammatik fra Danmark.',
  'fr-FR': 'Tu es dans une conversation vocale en temps réel (Real Time API). Parle en français de France, avec un accent français de France (pas québécois, pas belge, pas africain). Vocabulaire : voiture, téléphone, parking, appartement, petit-déjeuner.',
  'fr': 'Tu es dans une conversation vocale en temps réel (Real Time API). Parle en français de France, avec un accent français de France (pas québécois, pas belge, pas africain). Vocabulaire : voiture, téléphone, parking, appartement, petit-déjeuner.',
};

/**
 * Creates an identity assertion tool with enum-constrained role and language parameters.
 * The AI can only return one of the provided role values and the correct dialect label,
 * making client-side validation deterministic (exact === match).
 */
export function createIdentityAssertionTool(aiRole: string, userRole: string, languageCode: string): ToolDefinition {
  // No fallbacks (AI_INSTRUCTIONS §"NUNCA USAR FALLBACKS"): every input here is
  // required. The my_language enum is the anchor that prevents dialect drift
  // (e.g. Brazilian PT). Silently building the tool without it — or with free-text
  // params — is exactly the failure mode we are fixing, so fail loud instead.
  if (!aiRole || typeof aiRole !== 'string' || !aiRole.trim()) {
    throw new Error('[identityTools] FATAL: aiRole is REQUIRED to build the identity assertion tool.');
  }
  if (!userRole || typeof userRole !== 'string' || !userRole.trim()) {
    throw new Error('[identityTools] FATAL: userRole is REQUIRED to build the identity assertion tool.');
  }
  const dialectLabel = languageCode ? LANGUAGE_DIALECT_LABELS[languageCode] : undefined;
  if (!dialectLabel) {
    throw new Error(`[identityTools] FATAL: languageCode "${languageCode}" has no dialect label. The my_language enum is the anchor that prevents dialect drift (e.g. Brazilian PT) and must never be dropped.`);
  }

  const properties: Record<string, any> = {
    my_role: {
      type: 'string',
      enum: [aiRole, userRole],
      description: 'Your role in this conversation. Must be exactly one of the allowed values.'
    },
    their_role: {
      type: 'string',
      enum: [aiRole, userRole],
      description: 'The other person\'s role in this conversation. Must be exactly one of the allowed values.'
    },
    my_language: {
      type: 'string',
      enum: [dialectLabel],
      description: 'The language and dialect you must use. Must be exactly the allowed value.'
    },
  };

  const required = ['my_role', 'their_role', 'my_language'];

  return {
    type: 'function',
    name: 'assert_identity',
    description: 'You MUST call this function silently before EVERY response to confirm your identity and language. Never mention this function, its arguments, or identity-assertion text in spoken output.',
    parameters: {
      type: 'object',
      properties,
      required,
    }
  };
}

// IDENTITY_ASSERTION_TOOL_FALLBACK removed: a free-text fallback dropped the
// dialect-anchoring enum and let Brazilian PT drift through. Per AI_INSTRUCTIONS
// (no fallbacks for required data), callers must supply aiRole/userRole/language
// and let createIdentityAssertionTool fail loud if any is missing.
